import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { VaultRequestError, VaultSDK, type AgentConnection, type Job } from '@cef-ai/vault-sdk';
import { connectCereWallet, registerCereWallet, resumeCereWallet, type CereWallet } from '@/lib/clients/cere-wallet/connect';
import { claimVault, defaultVaultName } from '@/lib/clients/vault/claim';
import { buildSigningSdk } from '@/lib/clients/vault/signing-sdk';
import { clearStoredVault, getStoredVault, setStoredVault } from '@/lib/clients/vault/local-store';
import { mintRefreshingVaultReadClient } from '@/lib/clients/vault/read-client';
import { fetchOrgs, type OrgSummary } from '@/lib/clients/vault/orgs';
import { deriveVaultState, type VaultUiState } from '@/lib/vault/state';
import { stepUpRequiredError, toDisplayError } from '@/lib/vault/step-up-error';
import { useAgentsAndCards } from '@/lib/vault/useAgentsAndCards';
import { useVaultJobs } from '@/src/lib/useVaultJobs';
import { useStableAgentOrder } from '@/src/lib/useStableAgentOrder';
import { vaultHandleToRecord, type VaultHandle, type VaultRecord } from '@/lib/vault/types';
import { ensureVaultDelegateGrant } from '@/lib/clients/vault/vault-delegate';
import { Snackbar } from '@cere/cere-design-system';
import { VaultBootstrapping } from '@/src/components/VaultBootstrapping';
import { VaultClaiming, type ClaimStep } from '@/src/components/VaultClaiming';
import { VaultConnectPrompt } from '@/src/components/VaultConnectPrompt';
import { VaultDisconnectedBanner } from '@/src/components/VaultDisconnectedBanner';
import { VaultShell } from '@/src/components/VaultShell';
import { VaultSidebar } from '@/src/components/VaultSidebar';
import { useConfig } from '@/src/config/context';
import { PaymentsProfileProvider } from '@/src/hooks/usePaymentsProfile';
import { VaultSessionProvider, type VaultSession } from '@/src/state/session';
import '@/src/styles/vault.css';

// Subset of the claim's progress kinds we care about for the "securing your
// wallet" UI hint. Two sources feed it: the repo-owned onboarding switch
// (`lib/clients/vault/onboarding.ts` emits the authorizing-gateway family) and
// vault-sdk 5.0.0's `ensure()`, whose NEW `authorizing-vault` kind fires around
// the on-chain NonTransfer proxy grant to vault-api — the consent step where
// the user's vault is allowed to store data on their behalf. The SDK emits
// more granular events; we collapse the noisy ones into the same waiting state.
type ProxyPhase = 'initializing' | 'waiting' | 'authorizing' | null;

function progressToProxyPhase(kind: string): ProxyPhase {
  if (kind === 'gateway-authorization-required' || kind === 'authorizing-gateway') return 'initializing';
  if (kind === 'authorization-submitted' || kind === 'awaiting-chain-confirmation') return 'waiting';
  if (kind === 'authorizing-vault') return 'authorizing';
  return null;
}

export function VaultLayout() {
  const config = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wallet, setWallet] = useState<CereWallet | null>(null);
  const [vault, setVault] = useState<VaultRecord | null>(null);
  const [vaultHandle, setVaultHandle] = useState<VaultHandle | null>(null);
  // The read-side twin of `vaultHandle`. Same vault, but bound to the Bearer
  // read SDK instead of the wallet-signing signing SDK, so anything driven by
  // a timer or an effect can use it without needing keys. Today that is the
  // jobs poll; `vaultHandle` stays the signing handle that agents.connect and
  // scopes.* go through, because those are user-initiated and have a gesture.
  const [readVaultHandle, setReadVaultHandle] = useState<VaultHandle | null>(null);
  const [sdk, setSdk] = useState<VaultSDK | null>(null);
  const [readSdk, setReadSdk] = useState<VaultSDK | null>(null);
  const [claiming, setClaiming] = useState(false);
  // Distinct from `claiming` on purpose. `claiming` drives `deriveVaultState`
  // to 'claiming', which swaps the whole page for the claim animation — right
  // for the multi-step claim, wrong for the wallet-creation ceremony that
  // precedes it. If creation set `claiming`, the connect prompt would unmount
  // the instant the user clicked: the "Creating…" state could never render,
  // and a failed creation would drop the user back to a bare prompt with no
  // sign anything had happened. `registering` covers exactly the
  // `registerCereWallet` call, keeping the prompt mounted until the claim
  // actually begins — and that call is now LONG (the wallet's popup waits for
  // an emailed one-time code), so the prompt must survive minutes, not seconds.
  //
  // SUPERSEDED detail: this comment used to add "a failed creation would drop
  // the user back to a bare prompt with the typed email gone", and cited the
  // form's own state as a second thing worth keeping mounted. Neither exists
  // since S0 — the wallet origin collects and verifies the address, so vault
  // has no create-wallet form and no typed email to lose.
  const [registering, setRegistering] = useState(false);
  // Guards `reconnectWallet` the same way `registering` guards `createWallet`:
  // without it a double-click on "Reconnect wallet" fires two
  // `connectCereWallet`/`hydrateWalletSession` calls, i.e. two wallet connects
  // and two Bearer delegation mints. `startReadSdk`'s generation guard (see
  // `readRefreshGenRef`) already stops the second mint's scheduler from being
  // orphaned, but that is a second line of defence for races this flag can't
  // reach (unmount, disconnect) — it does not stop the redundant connect
  // attempt itself, which is this flag's job.
  const [reconnecting, setReconnecting] = useState(false);
  const [claimStep, setClaimStep] = useState<ClaimStep>(0);
  const [error, setError] = useState<string | null>(null);
  const [proxyPhase, setProxyPhase] = useState<ProxyPhase>(null);
  const [hasStoredVault, setHasStoredVault] = useState(false);
  // True once the boot existence-probe (below) has confirmed via a 404 that
  // the CONNECTED wallet has no vault. Distinct from `!vault` alone: that is
  // also true while the probe is still in flight, and the render guard needs
  // to tell "still checking" (show the splash) apart from "checked, there is
  // none" (fall through to the connect/claim prompt) without waiting on a
  // network round-trip on every render. Reset on any explicit
  // disconnect/wallet-switch and at the start of a fresh probe, so a stale
  // `true` from a previous wallet can't suppress the splash for a new one.
  const [vaultNotFound, setVaultNotFound] = useState(false);
  // The wallet whose probe produced that `true`. Lets the boot effect tell "this
  // wallet is already known to have no vault" (skip — a read-token re-mint must
  // not re-probe) from "a stale `true` left over from a DIFFERENT wallet"
  // (re-probe, which is what the reset below was written for).
  const notFoundForWalletRef = useRef<CereWallet | null>(null);
  // True from mount until the initial auto-reconnect attempt resolves.
  // While true we render a verifying/initializing splash instead of the
  // "Open your Vault / Reconnect wallet" prompt — otherwise a returning
  // user briefly sees buttons before they're transparently signed back in.
  const [bootstrapping, setBootstrapping] = useState(true);
  const bootstrapDoneRef = useRef(false);
  // Memoizes the lazily-resolved signing handle for the life of the session,
  // so the signed `vault.current()` round-trip happens at most once and only
  // ever inside a user gesture. Cleared by the explicit
  // `signingHandleRef.current = null` in `disconnect` and
  // `chooseDifferentWallet` — and ONLY by those two lines. `setSdk(null)` on
  // those same paths does not clear it: a ref is not state, so it survives the
  // re-render that installs the next wallet's `sdk`, and `??=` below would
  // then hand wallet B a handle resolved from — and signed by — wallet A.
  // Do not delete either assignment on the theory that nulling the SDK covers
  // it; those two lines are what makes the wallet swap safe.
  const signingHandleRef = useRef<Promise<VaultHandle> | null>(null);
  // Cancels the read instance's silent re-mint timer. Held in a ref because it
  // is armed inside an async handler and must be reachable from every teardown
  // point (unmount, `disconnect`, `chooseDifferentWallet`) without re-rendering.
  const stopReadRefreshRef = useRef<(() => void) | null>(null);
  // Monotonic counter naming the CURRENT read-refresh attempt. `startReadSdk`
  // publishes its disposer only after an `await`, so without this a second
  // concurrent call would clobber the first disposer reference and strand a
  // scheduler that NO teardown path can reach: it would keep calling
  // `requestDelegation` and `setReadSdk` every ~48 min for a wallet the user
  // has already left. The scheduler's own `disposed` flag cannot help,
  // because the thing that gets lost is the `dispose` reference itself.
  // Every start and every teardown bumps this, so a mint that resolves out
  // of turn disposes itself instead of publishing.
  //
  // "Reconnect wallet" now has its own busy flag (`reconnecting`, below),
  // which closes the same-button double-click this guard originally cited
  // as its motivating trigger — that specific case no longer reaches here.
  // This guard stays load-bearing for what a button flag cannot cover:
  // unmount/disconnect landing mid-mint, and a cross-button race
  // ("Reconnect wallet" then "Use a different wallet", two different
  // functions, neither aware of the other's in-flight state — still live in
  // production, tracked as #48, deliberately not fixed here because doing so
  // would need this guard re-pointed rather than removed). Do not delete
  // this on the theory that the button guard made it dead code.
  const readRefreshGenRef = useRef(0);
  // Which `readSdk` instance `readVaultHandle` was resolved from. A `Vault`
  // handle owns the `VaultHttpClient` it was built with, and that owns the
  // `DelegationAuthProvider` with its token baked in — so the handle does NOT
  // follow a token refresh. This is what lets the re-bind effect below tell
  // "handle already matches the current SDK" from "the SDK was replaced by a
  // silent re-mint and the handle is now carrying an expiring token."
  const readHandleSourceRef = useRef<VaultSDK | null>(null);
  const [reauthorizing, setReauthorizing] = useState(false);
  const [reauthError, setReauthError] = useState<string | null>(null);
  // Outcome of the vault's write-delegation grant — the on-chain NonTransfer
  // proxy to vault-api, submitted by `vault.ensure()` during the claim.
  // Persisted with the stored vault record, so it survives the reload.
  //
  //   `true`      — a claim ran to completion (the grant step throws out of
  //                 `ensure` on failure), or a re-authorize confirmed it.
  //   `false`     — a LEGACY record from the old gateway-grant track, which
  //                 never succeeded for anyone. Kept as "unconfirmed", never
  //                 rewritten to true by guesswork.
  //   `undefined` — unknown (record predates the field, or another device).
  //
  // It drives the Settings control ONLY — never a warning banner. The real
  // signal for the banner is `vault.status === 'disconnected'` (below).
  const [delegateGranted, setDelegateGranted] = useState<boolean | undefined>(undefined);
  // Organizations (the switcher's list) + the orgs section's selection. Both
  // live here so the whole one-scroll shares one org context; `orgs` is read
  // at boot over the BEARER instance (effect below) — no signing, per the
  // same rule as every other boot read in this layout. `orgsFailed` is the
  // read's outcome flag so the switcher can tell "failed" from "empty".
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [orgsFailed, setOrgsFailed] = useState(false);
  const [activeOrgVaultId, setActiveOrgVaultId] = useState<string | null>(null);

  /** Record the grant outcome in both places at once — React state for this
   *  render, and the stored record so it outlives the mount. */
  function rememberDelegateGrant(ok: boolean) {
    setDelegateGranted(ok);
    const stored = getStoredVault();
    if (stored) setStoredVault({ ...stored, delegateGranted: ok });
  }

  const data = useAgentsAndCards({
    wallet,
    sdk: readSdk,
    vaultBaseUrl: config.vaultBaseUrl,
    vaultId: vault?.vaultId ?? null,
  });
  const { agents, agentCards, scopes, loaded: agentsLoaded, refreshScopes, refreshAgents } = data;
  useEffect(() => { if (data.error) setError(data.error); }, [data.error]);

  // Single vault-wide jobs poll (spec: one fetch feeds everything). Feeds both
  // the activity feed and the recency order shared by the sidebar and the
  // home blocks — computed here, at the one place that owns both surfaces, so
  // the sidebar table-of-contents and the block scroll are always in lockstep.
  const { jobs, loaded: jobsLoaded } = useVaultJobs(readVaultHandle, { limit: 40 });
  const orderedAgents = useStableAgentOrder(agents, jobs, jobsLoaded);

  // Scroll-spy: which block is currently in view. Home reports it as the user
  // scrolls; it drives the sidebar highlight (so the active tab tracks the
  // scroll, not just clicks). Kept separate from `selectedAnchor` (the URL /
  // click target) so updating it never re-triggers the scroll-to-anchor effect.
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);

  // Bootstrap: try to load current vault for connected wallet.
  useEffect(() => {
    if (!wallet || !readSdk || vault || claiming) return;
    // A silent read-token re-mint replaces `readSdk`'s identity, which re-runs
    // this effect. When a vault is loaded that is harmless (`vault` above
    // returns), but in the connected-wallet-with-NO-vault state (`vaultNotFound`
    // true, `vault` null) it is not: the reset below would fire, flip the render
    // guard, and swap `VaultConnectPrompt` for the "Initializing…" splash
    // mid-session — pulling the prompt out from under a registration that is
    // still waiting on the wallet popup's emailed code, which is the exact
    // regression the `registering` flag exists to prevent. (Pre-S0 the thing
    // lost here was a half-typed create-wallet email; vault no longer collects
    // one, but the prompt is still what must not be swapped out.) Nothing on
    // the server changed, so do not re-probe. Keyed on the WALLET rather than
    // a bare `vaultNotFound` check so the stale-`true`-from-a-previous-wallet
    // case the reset was written for still re-probes: `createWallet` swaps
    // `wallet` while `vaultNotFound` is still true, and a failed claim lands
    // right back here.
    if (vaultNotFound && notFoundForWalletRef.current === wallet) return;
    if (!config.gatewayPublicKey) return; // claim path needs it (onboarding addProxy target); wait until config ready
    // Capture the narrowed values: TS narrowing from the guards above does not
    // cross the async IIFE closure boundary, and buildSigningSdk needs strings.
    const s3Endpoint = config.s3Endpoint;
    if (!s3Endpoint) return; // signing SDK derives the onboarding gateway base from this; wait until set
    let cancelled = false;
    // Reset before every fresh attempt (not just on success) — a stale `true`
    // from a PREVIOUS wallet's 404 must not suppress the splash for a new
    // wallet whose probe hasn't resolved yet. Harmless no-op on the very
    // first probe, where it is already false.
    setVaultNotFound(false);
    notFoundForWalletRef.current = null;
    (async () => {
      try {
        // Existence probe via the BEARER read instance (no wallet prompt for
        // brand-new users who have no vault yet -> 404 -> connect prompt).
        // Its handle is kept, not discarded: it is the one that backs the
        // vault-wide jobs poll below. That poll runs on a 10s timer, and a
        // timer has no user gesture — so it must never be able to sign. See
        // lib/clients/vault/read-client.ts.
        const readHandle = await readSdk.vault.current();
        if (cancelled) return;
        readHandleSourceRef.current = readSdk;
        setReadVaultHandle(readHandle);
        // The signing SDK is CONSTRUCTED here but deliberately not CALLED:
        // building a VaultSDK signs nothing, whereas `vault.current()` on it
        // would — and this runs on boot with no user gesture. The signing
        // handle is resolved lazily instead, inside whichever click needs it
        // (see `signingHandle` below).
        const signingSdk = buildSigningSdk(wallet, {
          vaultBaseUrl: config.vaultBaseUrl,
          s3Endpoint,
          ...(config.garUrl ? { garUrl: config.garUrl } : {}),
          ...(config.rpcEndpoint ? { chainUrl: config.rpcEndpoint } : {}),
        });
        setSdk(signingSdk);
        setVaultHandle(readHandle);
        setVault(vaultHandleToRecord(readHandle));
        // Preserve whatever name the user actually claimed with instead of
        // clobbering it — only compute a fallback when there is no stored
        // record at all (e.g. a vault claimed from another device/session).
        //
        // SUPERSEDED reasoning, kept because it is the obvious thing to
        // re-derive: this used to say `connect()`/`resume()` never populate
        // email/name (only `createWallet`'s `entered` argument did), so
        // `defaultVaultName` would fall back to a pubkey-derived seed on every
        // reconnect. Since S0 the wallet reports its own VERIFIED email on all
        // three paths, so that fallback is no longer what a recompute would
        // produce. The preference for the stored name stands regardless, and
        // now for a plainer reason: the stored name is the one that was sent to
        // `vault.ensure`, and the user may have edited it since.
        const existingStored = getStoredVault(wallet.address);
        // Carry the grant outcome across the reload. Spreading it back is what
        // makes the warning and the Settings control honest after a refresh —
        // and `undefined` must survive as `undefined` (unknown), never collapse
        // to `false`, or every pre-existing user gets a grant failure invented
        // for them on their next load.
        setDelegateGranted(existingStored?.delegateGranted);
        setStoredVault({
          walletAddress: wallet.address,
          vaultId: readHandle.id,
          vaultName: existingStored?.vaultName ?? defaultVaultName(wallet),
          ...(existingStored?.delegateGranted !== undefined
            ? { delegateGranted: existingStored.delegateGranted }
            : {}),
        });
      } catch (e) {
        if (e instanceof VaultRequestError && e.status === 404) {
          if (!cancelled) {
            // No vault for the CONNECTED wallet — not an error, just a user
            // who hasn't claimed one (yet). Route to the connect/claim
            // prompt via `vaultNotFound` rather than leaving `wallet` set /
            // `vault` null, which used to pin the "initializing" splash
            // forever (no effect re-fires, reload reproduces it).
            //
            // A stored record can still be lying about this: the
            // wallet-source migration's boot-resume `getStoredVault()` call
            // (no address argument, above) accepts a record from ANY
            // wallet, so a stale pre-passkey record for a DIFFERENT address
            // satisfies `hasStoredVault` and offers "Reconnect" for a
            // wallet that was never claimed. Clear it. A record that
            // matches the connected address is a still-valid claim marker —
            // `reconnectWallet`'s catch (below) deliberately never clears
            // that, and this must not either.
            //
            // Keeping it does NOT mean the screen offers "Reconnect": that
            // would be a dead button (reconnecting re-resolves to the SAME
            // wallet, re-mints a Bearer delegation and is swallowed by the
            // `notFoundForWalletRef` guard above, while hiding the claim CTA
            // that would actually fix the state). `vaultNotFound` is passed to
            // `VaultConnectPrompt` for exactly that reason and routes the
            // primary button to `onStartClaim` — see its `offerReconnect`.
            // The flag below stays keyed to "a record exists" so the
            // "Use a different wallet" escape hatch, which is also gated on
            // it, survives this state.
            const stale = getStoredVault();
            if (stale && stale.walletAddress !== wallet.address) {
              clearStoredVault();
              setHasStoredVault(false);
            }
            notFoundForWalletRef.current = wallet;
            setVaultNotFound(true);
          }
          return;
        }
        if (!cancelled) setError(toDisplayError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [wallet, readSdk, vault, claiming, vaultNotFound, config.vaultBaseUrl, config.s3Endpoint, config.gatewayPublicKey, config.garUrl, config.rpcEndpoint]);

  // Re-bind the jobs-poll handle after a silent token re-mint. Swapping
  // `readSdk` alone is not enough: `readVaultHandle` carries the
  // `VaultHttpClient` — and therefore the token — of the instance it was
  // resolved from, so the 10s jobs poll would keep authenticating with the
  // expiring token and freeze the activity feed, which is the exact failure
  // the re-mint exists to prevent.
  //
  // Scoped to a handle that ALREADY exists on purpose: this must never probe
  // for a vault on its own. That is the boot effect's job above, and a 404
  // here (a connected wallet with no vault) is routing information, not an
  // error worth surfacing.
  useEffect(() => {
    if (!readSdk || !readVaultHandle) return;
    if (readHandleSourceRef.current === readSdk) return;
    let cancelled = false;
    void (async () => {
      try {
        const rebound = await readSdk.vault.current();
        if (cancelled) return;
        readHandleSourceRef.current = readSdk;
        setReadVaultHandle(rebound);
      } catch (e) {
        if (!cancelled) setError(toDisplayError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [readSdk, readVaultHandle]);

  // The organizations switcher's list — one Bearer read at boot (own vault +
  // memberships; see fetchOrgs). Deliberately its OWN effect rather than a
  // tail on the boot probe above: a `mine()` failure must not be able to
  // route the user to the claim prompt, which the boot effect's 404 handling
  // owns. Keyed on the vault ID (not the vault object) so delegate-status
  // updates that swap the `vault` record do not re-fetch, and on `readSdk`
  // because a silent re-mint swaps the instance's identity — the same
  // refetch-on-sdk-change contract `useAgentsAndCards` already follows.
  const bootVaultId = vault?.vaultId ?? null;

  /** Commit a successful orgs read: the list, plus both outcome flags. */
  const applyOrgs = useCallback((next: OrgSummary[]) => {
    setOrgs(next);
    setOrgsLoaded(true);
    setOrgsFailed(false);
  }, []);

  /** The write side's refresh (wizard rename, later member edits): one more
   *  Bearer read, through the same error channel as the boot read. */
  const refreshOrgs = useCallback(async () => {
    if (!readSdk || !bootVaultId) return;
    try {
      applyOrgs(await fetchOrgs(readSdk));
    } catch (e) {
      // Same contract as the boot path: non-fatal, surfaced, and recorded as
      // a FAILED read so the switcher never reads it as an empty membership.
      setOrgsLoaded(true);
      setOrgsFailed(true);
      setError(toDisplayError(e));
    }
  }, [readSdk, bootVaultId, applyOrgs]);

  useEffect(() => {
    if (!readSdk || !bootVaultId) return;
    let cancelled = false;
    void (async () => {
      try {
        const next = await fetchOrgs(readSdk);
        if (!cancelled) applyOrgs(next);
      } catch (e) {
        // Surfaced through the shared error channel like every other
        // data-load failure here, but non-fatal: the session is complete
        // without orgs, and the switcher falls back to My-vault-only — its
        // failed state, never the "you belong to nothing" claim.
        if (!cancelled) {
          setOrgsLoaded(true);
          setOrgsFailed(true);
          setError(toDisplayError(e));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [readSdk, bootVaultId, applyOrgs]);

  /** The setup wizard renamed the wallet's own vault: persist the new name
   *  the way a claim would have (vaultDisplayName is derived from the stored
   *  record at render time, not state), and force the re-render that re-reads
   *  it. The orgs refresh the wizard performs next would also re-render, but
   *  relying on that alone would make the label depend on call order between
   *  two session methods. The owner entry in `orgs` is renamed IN PLACE too:
   *  the switcher's My-vault caption reads it (`owner?.name ?? myVaultName`),
   *  so a copy-the-stale-list re-render would keep the pre-rename name
   *  whenever that follow-up refresh fails. */
  function noteVaultRenamed(name: string) {
    const stored = getStoredVault();
    if (stored) setStoredVault({ ...stored, vaultName: name });
    setOrgs((o) => o.map((e) => (e.isOwner ? { ...e, name } : e)));
  }

  // Stop the read-token re-mint when the layout unmounts. Own effect with no
  // deps so it runs exactly once, at teardown: a scheduler left armed would
  // keep calling `requestDelegation` (and `setState`) for a component that is
  // gone. Reachable in production, not just under StrictMode — `/connect` is a
  // sibling route, so navigating there unmounts this layout.
  // `disconnect`/`chooseDifferentWallet` clear it on their own paths.
  //
  // Touches the refs directly rather than calling `stopReadRefresh`, so the
  // empty dep array stays honest. The generation bump is the load-bearing half:
  // a mint still in flight at unmount would otherwise publish its disposer
  // AFTER this cleanup nulled the ref, re-arming an orphan on a dead component.
  useEffect(() => () => {
    readRefreshGenRef.current += 1;
    stopReadRefreshRef.current?.();
    stopReadRefreshRef.current = null;
  }, []);

  // Auto-reconnect from local storage. Drives the `bootstrapping` flag
  // so the UI can show a verifying splash instead of flashing the connect
  // prompt at returning users.
  //
  // Uses `resumeCereWallet` (silent session check — never a WebAuthn
  // ceremony, never a popup) rather than `reconnectWallet`/`login()`. On
  // boot there is no user gesture, so if the refresh cookie is expired a
  // ceremony would fall back to a popup and get `popup-blocked`. That used
  // to be caught here and treated as a reconnect failure, wiping the
  // stored vault-claim record even though the user still has a vault —
  // they just have no active session. `resume()` returns `null` cleanly
  // instead, so a lapsed session now just surfaces the "Reconnect" CTA.
  useEffect(() => {
    if (bootstrapDoneRef.current) return;
    // `wallet` guards against re-running the boot resume once a session is
    // already connected: the cleanup resets `bootstrapDoneRef` (for StrictMode,
    // below) and this effect's deps include `claiming`, so a failed claim
    // (claiming false→true→false, vault still null) would otherwise re-arm and
    // re-resume. Boot resume is a once-per-connect concern.
    if (wallet || vault || claiming) return;
    bootstrapDoneRef.current = true;
    const stored = getStoredVault();
    setHasStoredVault(Boolean(stored));
    if (!stored) {
      setBootstrapping(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const w = await resumeCereWallet(config.appEnv);
        if (cancelled) return;
        if (w) {
          await hydrateWalletSession(w);
        } else {
          // No live session — the vault-claim record is still valid, the
          // user just needs to reconnect. Do NOT clearStoredVault().
          setHasStoredVault(true);
        }
      } catch (e) {
        if (!cancelled) setError(toDisplayError(e));
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    // Reset the guard on cleanup so React 18 StrictMode's double-invoke
    // (mount → cleanup → mount, dev only) re-runs the resume on the surviving
    // invocation. Without this, the first run set the ref, its cleanup set
    // `cancelled`, and the second run early-returned — so the first run's
    // `finally` skipped `setBootstrapping(false)` and nothing ever cleared the
    // "Verifying wallet…" splash.
    return () => {
      cancelled = true;
      bootstrapDoneRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault, claiming]);

  const uiState: VaultUiState = useMemo(
    () => deriveVaultState({
      walletConnected: wallet !== null,
      claiming,
      vaultId: vault?.vaultId ?? null,
      agentCount: agents.length,
    }),
    [wallet, claiming, vault, agents],
  );

  // The persisted name is authoritative for display: it's the name actually
  // sent to `vault.ensure` at claim time, and the user may have changed it
  // since. Only fall back to recomputing when there's no stored record at all.
  //
  // SUPERSEDED reasoning: this used to justify the preference by saying
  // email/name were available only from `createWallet`, so a session hydrated
  // via connect()/resume() would recompute a pubkey-derived name. Since S0 the
  // wallet carries a verified email on every path, so a recompute would now
  // usually produce an email-derived name rather than a pubkey-derived one —
  // still not the claimed name, so the stored value still wins, but not for
  // the reason written here before.
  const vaultDisplayName = vault
    ? (getStoredVault(wallet?.address)?.vaultName ?? (wallet ? defaultVaultName(wallet) : vault.vaultId))
    : 'your-vault';
  const walletDisplayName = wallet?.name ?? wallet?.email;

  // The one-scroll page is anchor-driven: the sidebar / activity feed set
  // `?anchor=` and home scrolls the matching block into view. (The old
  // multi-view `?view=` machinery went with the deleted tab surface.)
  const selectedAnchor = searchParams.get('anchor');

  // Stable identity: threaded down into the memo()'d AgentBlock (via
  // VaultSidebar/AgentsPage). A fresh function every render would bust that
  // memo on every ~10s useVaultJobs poll — re-rendering every agent block
  // and its live widget iframe for no reason. Deps are exactly what the body
  // reads: `location.pathname` (not the whole `location` object, which is a
  // new reference on every route object even when the pathname is
  // unchanged), plus `navigate`/`setSearchParams` (stable per react-router).
  const onSelectAnchor = useCallback((anchor: string) => {
    const params = new URLSearchParams();
    if (anchor) params.set('anchor', anchor);
    if (location.pathname !== '/') {
      const qs = params.toString();
      navigate(qs ? `/?${qs}` : '/');
    } else {
      setSearchParams(params);
    }
  }, [location.pathname, navigate, setSearchParams]);

  async function disconnect() {
    try { await wallet?.logout(); } catch { /* wallet already gone */ }
    clearStoredVault();
    setHasStoredVault(false);
    setVaultNotFound(false);
    notFoundForWalletRef.current = null;
    bootstrapDoneRef.current = false;
    setWallet(null);
    setVault(null);
    setVaultHandle(null);
    // Must be cleared alongside `vaultHandle`, or the 10s jobs poll keeps
    // running against a logged-out session (useVaultJobs only stops when its
    // handle goes null), and the memoized signing handle would be reused by
    // the NEXT wallet to sign in.
    setReadVaultHandle(null);
    signingHandleRef.current = null;
    // Same reason, one layer up: the read token's re-mint timer outlives
    // `readSdk` unless it is cancelled here, and a scheduler still minting
    // delegations for a wallet the user explicitly left is the logged-out-
    // session bug in its purest form.
    stopReadRefresh();
    readHandleSourceRef.current = null;
    setSdk(null);
    setReadSdk(null);
    // Orgs belong to the wallet that read them: drop the list and the
    // selection so a stale org from this wallet can't follow the next one in.
    setOrgs([]);
    setOrgsLoaded(false);
    setOrgsFailed(false);
    setActiveOrgVaultId(null);
    setSearchParams(new URLSearchParams());
    if (location.pathname !== '/') navigate('/');
  }

  // Shared by both the boot (resume) and gesture (reconnect button) paths:
  // mint a Bearer delegation and build the read SDK from it, then hydrate the
  // wallet + readSdk state.
  //
  // This is THE call site the Bearer decision exists for. It runs on boot with
  // no user gesture, and `resume()` restores only the bearer session — the
  // PRF-derived signing keys are never persisted, so `hasKeys` is false after
  // every reload. A direct-sign read here would raise `needs-step-up`, whose
  // popup must open synchronously inside a gesture, and the browser would
  // block it. `requestDelegation` needs only `isAuthenticated`, so this path
  // is silent and popup-free — which is what restores the old wallet's
  // rehydrate-and-show-data behaviour (G2).
  async function hydrateWalletSession(w: CereWallet) {
    setWallet(w);
    // `startReadSdk` owns the `setReadSdk` write, so a superseded mint cannot
    // install itself as the session's read instance on its way out.
    await startReadSdk(w);
  }

  /**
   * Mint the Bearer read instance AND keep it minted. `VAULT_READ_TTL` is 1h,
   * and `DelegationAuthProvider` holds its token as `private readonly` — so
   * without a scheduled re-mint every data-plane read in a tab left open past
   * the hour 401s, and `useVaultJobs`' bare catch turns that into an activity
   * feed that silently freezes instead of erroring.
   *
   * Safe on a timer: `requestDelegation` needs only `isAuthenticated` — no
   * keys, no ceremony, no popup — so nothing here signs without a gesture.
   */
  async function startReadSdk(w: CereWallet): Promise<VaultSDK> {
    // Never leave two schedulers running: a reconnect through this path while
    // a previous session's timer is armed would otherwise keep re-minting for
    // the wallet the user just left. This also bumps the generation, which is
    // what invalidates any mint already in flight.
    stopReadRefresh();
    const gen = readRefreshGenRef.current;
    const { sdk: read, dispose } = await mintRefreshingVaultReadClient(
      w,
      {
        vaultBaseUrl: config.vaultBaseUrl,
        ...(config.garUrl ? { garUrl: config.garUrl } : {}),
      },
      {
        onRefresh: setReadSdk,
        // Surfaced, not swallowed — the same channel every other data-load
        // failure in this layout uses. The scheduler keeps the current
        // instance and retries, so this is a warning, not a session teardown.
        onError: (e) => setError(toDisplayError(e)),
      },
    );
    if (gen !== readRefreshGenRef.current) {
      // Superseded while we were awaiting: another `startReadSdk`, an unmount,
      // or a disconnect ran in the meantime. Publishing `dispose` here would
      // overwrite the winner's reference and orphan one of the two schedulers
      // beyond the reach of every teardown path, so dispose OURS and write no
      // state. The SDK is still returned because it is a perfectly valid
      // (just unscheduled) client, and `claimWithWallet` reads through it.
      dispose();
      return read;
    }
    stopReadRefreshRef.current = dispose;
    setReadSdk(read);
    return read;
  }

  function stopReadRefresh() {
    readRefreshGenRef.current += 1;
    stopReadRefreshRef.current?.();
    stopReadRefreshRef.current = null;
  }

  // Single source of truth for the delegate/connected state: the layout owns
  // vault.status, which drives both the disconnected banner (below) and the
  // Settings "Storage" control (via the `delegateActive` prop). SettingsPage
  // calls this after a successful re-authorize so the banner disappears
  // immediately, instead of only flipping local child state.
  function setDelegateActive(active: boolean) {
    setVault((v) => (v ? { ...v, status: active ? 'active' : 'disconnected' } : v));
    // Settings only calls this after a re-authorize CONFIRMED the on-chain
    // grant, so it is a real observation of the grant's existence — persist
    // it. Without this, a successful re-authorize would clear the banner for
    // one mount and the stale state would bring it straight back on reload.
    rememberDelegateGrant(active);
  }

  async function reauthorizeDelegate() {
    if (!wallet || !vault || !sdk) return;
    if (!config.rpcEndpoint) {
      setReauthError('Chain RPC endpoint unavailable — cannot re-authorize the vault delegate.');
      return;
    }
    setReauthorizing(true);
    setReauthError(null);
    try {
      // Re-grant vault-api's on-chain NonTransfer write-delegation proxy —
      // the durable replacement for the removed gateway delegate-grant
      // (`registerVaultDelegate`). Idempotent: when a satisfying grant is
      // already on chain this is a read + `alreadyPresent`, no signature.
      await ensureVaultDelegateGrant({
        signer: wallet.signingSigner,
        chainUrl: config.rpcEndpoint,
        vaultApiUrl: config.vaultBaseUrl,
      });
      // Refresh status from the source of truth so the banner clears only when
      // vault-api confirms it can mint credentials again.
      const refreshed = await sdk.vault.current();
      setVaultHandle(refreshed);
      setVault(vaultHandleToRecord(refreshed));
      // The grant was confirmed on chain, so record it durably too.
      rememberDelegateGrant(true);
    } catch (e) {
      setReauthError(e instanceof Error ? e.message : String(e));
    } finally {
      setReauthorizing(false);
    }
  }

  async function reconnectWallet() {
    // Same shape as `createWallet`/`startClaim`'s
    // `if (claiming || registering) return;`, but note what it actually
    // guards: `reconnecting` is read from the render closure, so on a real
    // double-click this only ever observes `false` — the second click lands
    // before the re-render that would make it `true`. What actually stops
    // that double-click is `VaultConnectPrompt` disabling the button once
    // `setReconnecting(true)` (below) commits. This early-return is
    // defence-in-depth for a call site that isn't behind that button —
    // there is none today — kept for the same reason the sibling
    // `registering`/`claiming` guards keep theirs: consistency, and cheap
    // insurance against a future caller.
    if (reconnecting) return;
    setReconnecting(true);
    setError(null);
    try {
      const w = wallet ?? await connectCereWallet(config.appEnv);
      await hydrateWalletSession(w);
    } catch (e) {
      // Deliberately does NOT clearStoredVault(). The stored record is the
      // user's vault-CLAIM marker, not a session — nothing reachable here
      // invalidates it. This catch now covers two retryable failures: a
      // cancelled/blocked wallet connect, and `hydrateWalletSession`'s Bearer
      // delegation mint, which is a plain network round-trip that a gateway
      // blip fails. Clearing here wiped a still-valid claim and downgraded the
      // CTA from "Reconnect wallet" to "Connect wallet", destroying the exact
      // recovery path a retry needed — the same mistake the boot-resume path
      // at :203 already avoids. Losing the claim record is reserved for the
      // cases that actually mean it: `disconnect` and `chooseDifferentWallet`,
      // both explicit user intent.
      setError(toDisplayError(e));
    } finally {
      // Cleared on BOTH the success and failure paths — a failed reconnect
      // must leave the button usable again, or it strands the user on their
      // only recovery control.
      setReconnecting(false);
    }
  }

  // Shared post-wallet claim: identical for connect-then-claim and
  // create-then-claim, so wallet creation costs exactly one passkey ceremony.
  async function claimWithWallet(w: CereWallet) {
    setWallet(w);

    // ==========================================================================
    // THIS MUST STAY AHEAD OF EVERY `await` IN THIS FUNCTION.
    //
    // The gate decides whether a signature that lands LATER than this click is
    // possible at all, and it exists to refuse to start rather than to strand
    // the user mid-claim. So it has to precede the work it is refusing:
    // `startReadSdk` below is a `requestDelegation` network round-trip we would
    // be paying for and then discarding, and `claimVault` after it is where the
    // ~12-30s `add_proxy` finalization lives — the irreversible step this is
    // all about.
    //
    // SUPERSEDES the `warmUp()` gate this line used to hold (issue #41).
    // `warmUp()` was removed in @cef-ai/wallet 3.0.0. Its position was
    // ACTIVATION-relevant: it opened a popup, and every awaited round-trip
    // above it spent part of Chrome's ~5s transient-activation budget before
    // the popup was requested. `ceremonySurface()` opens nothing and is
    // synchronous, so that particular race is gone — but the ORDER still
    // matters for the reason above, which is why this block did not simply move.
    //
    // `claimVault` checks the same thing again, and that is intentional rather
    // than redundant: it is a public export reached from `VaultConnectFlow`
    // without passing through here, so its own precondition has to hold
    // independently. The duplicate costs one synchronous property read.
    //
    // 'popup' means a signature from here would need a `window.open`, which the
    // browser blocks without a live gesture. `startClaim` / `createWallet` both
    // catch this throw, render `toDisplayError` (the actionable reconnect line,
    // via the `needs-step-up` code) and drop the user back to
    // `VaultConnectPrompt`, whose primary control re-runs the claim from a
    // fresh click — safe, because the claim is idempotent end to end.
    //
    // NOT INFALLIBLE, and weaker than what it replaces on two engines:
    // recognising a policy refusal needs a Chromium-only API, so on Safari and
    // Firefox this reports 'iframe' and never fires. See the long note in
    // `lib/clients/vault/claim.ts` and wallet#77.
    //
    // Ordering pinned by `tests/unit/routes/vault-layout-ceremony-surface.test.tsx`.
    // ==========================================================================
    if (w.ceremonySurface() === 'popup') throw stepUpRequiredError();

    // Same Bearer read instance as the boot path. The claim itself still signs
    // (see buildSigningSdk below) — that is user-initiated and has a gesture to
    // spend; only the reads move off signing.
    //
    // Kept in a local as well as in state: the poll handle below is resolved
    // from THIS instance rather than minting a second delegation for it. One
    // `requestDelegation` round-trip per claim, and no way for a blip on a
    // redundant second mint to leave the poll dead while a perfectly good
    // read client sits unused. The 1h TTL comfortably outlives the claim.
    //
    // Refreshing, like the boot path: the session that just claimed lives on
    // in this tab, so its read token has to be renewed for exactly the same
    // reason a resumed one does. `read` below is only the FIRST instance —
    // later ones reach `readSdk` through the scheduler's `onRefresh`.
    const read = await startReadSdk(w);

    if (!config.gatewayPublicKey) {
      setError('Gateway public key unavailable — vault claim cannot mint a delegation token.');
      setClaiming(false);
      return;
    }

    setClaimStep(1);
    const vaultName = defaultVaultName(w);
    setClaimStep(2);
    if (!config.s3Endpoint) {
      setError('S3 gateway endpoint unavailable — vault claim cannot run onboarding.');
      setClaiming(false);
      return;
    }
    const { record, handle, sdk: builtSdk } = await claimVault({
      wallet: w,
      vaultName,
      vaultBaseUrl: config.vaultBaseUrl,
      gatewayPublicKey: config.gatewayPublicKey,
      s3Endpoint: config.s3Endpoint,
      garUrl: config.garUrl,
      rpcEndpoint: config.rpcEndpoint,
      onProgress: (kind) => setProxyPhase(progressToProxyPhase(kind)),
    });
    setSdk(builtSdk);
    setVaultHandle(handle);
    // The write-delegation grant is no longer a best-effort post-claim step
    // whose failure is reported and swallowed: `ensure()` (tokenless path)
    // grants vault-api's NonTransfer proxy on-chain BEFORE claiming and
    // THROWS on grant failure, so reaching here means the delegation is in
    // place. `delegateGranted: true` below is therefore an observation of
    // the SDK's success, not a hope about it.
    setDelegateGranted(true);
    // Keep the jobs poll on Bearer after a fresh claim too, not just after a
    // boot resume — otherwise the poll silently reverts to the signing handle
    // for the rest of the session that just claimed.
    try {
      const readHandle = await read.vault.current();
      readHandleSourceRef.current = read;
      setReadVaultHandle(readHandle);
    } catch (e) {
      // Non-fatal: the claim succeeded. The activity feed just stays empty
      // rather than falling back to a signing poll. Not necessarily transient:
      // if the cause is a delegation scope mismatch, the next boot's probe
      // fails identically rather than recovering.
      console.error('claimWithWallet: post-claim read probe failed (non-fatal)', e);
    }
    setVault(record);
    setStoredVault({
      walletAddress: w.address,
      vaultId: record.vaultId,
      vaultName,
      delegateGranted: true,
    });
    setHasStoredVault(true);
    setClaiming(false);
  }

  async function startClaim() {
    if (claiming || registering) return;
    setError(null);
    setProxyPhase(null);
    setClaiming(true);
    setClaimStep(0);
    try {
      const w = wallet ?? await connectCereWallet(config.appEnv);
      await claimWithWallet(w);
    } catch (e) {
      setError(toDisplayError(e));
      setClaiming(false);
    }
  }

  async function createWallet() {
    if (claiming || registering) return;
    setError(null);
    setProxyPhase(null);
    setClaimStep(0);
    // Two phases, two flags. Phase 1 is the wallet's own register ceremony
    // (email → one-time code → passkey, all inside its popup): the prompt
    // stays mounted so "Create new wallet" can read "Creating…" and stay
    // disabled for the whole time, which is now MINUTES rather than seconds
    // because the reader has to fetch a code from their mailbox. Phase 2 is
    // the claim, which is what the full-page animation is for.
    //
    // There is no host-side form state left to preserve across a failure —
    // that was this split's other original reason, and S0 removed the form.
    // The split still earns its keep for the mounted-prompt half.
    setRegistering(true);
    try {
      const w = await registerCereWallet(config.appEnv);
      setRegistering(false);
      setClaiming(true);
      await claimWithWallet(w);
    } catch (e) {
      setError(toDisplayError(e));
      setRegistering(false);
      setClaiming(false);
    }
  }

  async function chooseDifferentWallet() {
    try { await wallet?.logout(); } catch { /* wallet already gone */ }
    clearStoredVault();
    setHasStoredVault(false);
    setVaultNotFound(false);
    notFoundForWalletRef.current = null;
    setWallet(null);
    setVault(null);
    setVaultHandle(null);
    // Must be cleared alongside `vaultHandle`, or the 10s jobs poll keeps
    // running against a logged-out session (useVaultJobs only stops when its
    // handle goes null), and the memoized signing handle would be reused by
    // the NEXT wallet to sign in.
    setReadVaultHandle(null);
    signingHandleRef.current = null;
    // Same reason, one layer up: the read token's re-mint timer outlives
    // `readSdk` unless it is cancelled here, and a scheduler still minting
    // delegations for a wallet the user explicitly left is the logged-out-
    // session bug in its purest form.
    stopReadRefresh();
    readHandleSourceRef.current = null;
    setSdk(null);
    setReadSdk(null);
    // Same orgs-hygiene reset as `disconnect`: the claim that follows will
    // re-read them for the new wallet, but the window in between must not
    // show the previous wallet's organizations.
    setOrgs([]);
    setOrgsLoaded(false);
    setOrgsFailed(false);
    setActiveOrgVaultId(null);
    setError(null);
    void startClaim();
  }

  if (uiState === 'connecting') {
    // While auto-reconnect is in flight, or the wallet just came back and
    // we're still resolving the vault record, render a loader. Only fall
    // through to the connect prompt once the user actually needs to act.
    // Hide the splash if there's an error to surface — the prompt can
    // render the alert and offer a retry.
    //
    // `!registering` matters, and is the second way the old `creating={claiming}`
    // wiring lost the form. After a failed claim the state is exactly
    // wallet-set + vault-null, and `createWallet` clears the error on entry —
    // so without this guard, submitting the create form swaps it for the
    // "Verifying wallet…" splash instead of showing "Creating…".
    //
    // `!vaultNotFound` matters too: `wallet && !vault` alone can't tell "the
    // existence probe is still in flight" apart from "it already ran and
    // 404'd — this wallet has no vault." Without it, a reconnected wallet
    // with no vault got pinned on this splash forever: no effect re-fires on
    // a settled 404, so reload reproduced it and `disconnect` /
    // `chooseDifferentWallet` were unreachable from here.
    if (!error && !registering && (bootstrapping || (wallet && !vault && !vaultNotFound))) {
      const stage = wallet ? 'initializing' : 'verifying';
      return <VaultBootstrapping stage={stage} />;
    }
    return (
      <VaultConnectPrompt
        configReady
        hasStoredVault={hasStoredVault}
        vaultNotFound={vaultNotFound}
        error={error}
        onReconnect={() => { void reconnectWallet(); }}
        onStartClaim={() => { void startClaim(); }}
        onUseDifferentWallet={() => { void chooseDifferentWallet(); }}
        onCreateWallet={() => { void createWallet(); }}
        creating={registering}
        reconnecting={reconnecting}
      />
    );
  }

  if (uiState === 'claiming') {
    // The `authorizing` copy is the consent step's OWN words: the on-chain
    // NonTransfer proxy granted during the claim is what lets vault-api write
    // storage on the user's behalf, and it lasts exactly until they revoke —
    // phrasing it as capability + duration is the honest description of what
    // the signature buys.
    const proxyMsg = proxyPhase === 'initializing'
      ? 'Initializing wallet…'
      : proxyPhase === 'waiting'
        ? 'Securing wallet…'
        : proxyPhase === 'authorizing'
          ? 'Allow your Vault to store data on your behalf — until you revoke'
          : null;
    return (
      <>
        <VaultClaiming step={claimStep} />
        <Snackbar open={Boolean(proxyMsg)} severity="info" message={proxyMsg ?? ''} />
        <Snackbar open={Boolean(error)} severity="error" message={error ?? ''} onClose={() => setError(null)} />
      </>
    );
  }

  // `!readSdk` in this guard holds only by teardown coincidence, and that is
  // worth recording: no path leaves `readSdk` null while the other four are
  // set, because every vault-setting path sets the read instance FIRST — the
  // boot probe and the re-bind effect both run only once `readSdk` exists,
  // and `claimWithWallet` awaits `startReadSdk` before `claimVault`. The
  // paths that null `readSdk` (`disconnect`, `chooseDifferentWallet`, the
  // unmount cleanup) null the wallet, vault, handle and signing SDK in the
  // same synchronous breath, so the earlier conditions already returned. The
  // term is belt-and-braces for a future path that forgets that order; do
  // not "simplify" it away, and do not build a path that relies on it firing
  // alone (a half-torn session rendering nothing forever).
  if (!wallet || !vault || !vaultHandle || !sdk || !readSdk) return null;

  // Resolve (and memoize) the wallet-signing handle. Every caller is inside a
  // click — agents.connect, scopes.*, the disconnect dialog — so if the wallet
  // is cold the step-up popup opens within that gesture and is not blocked.
  const signingHandle = (): Promise<VaultHandle> => {
    signingHandleRef.current ??= sdk.vault.current().catch((e: unknown) => {
      // Don't cache a rejection: a cancelled ceremony must not poison every
      // later consent action for the rest of the session.
      signingHandleRef.current = null;
      throw e;
    }) as Promise<VaultHandle>;
    return signingHandleRef.current;
  };

  const session: VaultSession = {
    wallet,
    vault,
    vaultHandle,
    signingHandle,
    sdk,
    readSdk,
    orgs,
    orgsLoaded,
    orgsFailed,
    activeOrgVaultId,
    setActiveOrgVaultId,
    refreshOrgs,
    noteVaultRenamed,
    agents,
    agentCards,
    agentsLoaded,
    scopes,
    vaultBaseUrl: config.vaultBaseUrl,
    vaultDisplayName,
    ...(walletDisplayName ? { walletDisplayName } : {}),
    disconnect: () => { void disconnect(); },
    delegateGranted,
    setDelegateActive,
    refreshScopes,
    refreshAgents,
  };

  const body = (
    <VaultShell
      sidebar={
        <VaultSidebar
          vaultDisplayName={vaultDisplayName}
          agentCount={agents.length}
          scopeCount={scopes.length}
          agents={orderedAgents}
          agentCards={agentCards}
          scopes={scopes}
          selectedAnchor={activeAnchor ?? selectedAnchor}
          onSelectAnchor={onSelectAnchor}
        />
      }
    >
      {/* Driven ONLY by the credential's own status — deliberately not by the
          delegate grant state.

          `vault.status === 'disconnected'` fires when vault-api cannot refresh
          its storage credential, which is the failure users care about. The
          on-chain NonTransfer proxy granted during the claim is what lets
          vault-api re-mint it; "Re-authorize" re-runs that grant
          (`ensureVaultDelegateGrant`, idempotent). Note there is no revoke
          button anywhere in the UI: removing the on-chain proxy needs a
          `Proxy.remove_proxy` extrinsic the SDK does not wrap yet — tracked
          as a follow-up alongside an sdk surface for it. */}
      {vault.status === 'disconnected' && (
        <VaultDisconnectedBanner
          busy={reauthorizing}
          error={reauthError}
          onReauthorize={() => { void reauthorizeDelegate(); }}
        />
      )}
      <Outlet context={{ selectedAnchor, error, onSelectAnchor, orderedAgents, jobs, setActiveAnchor }} />
      <Snackbar open={Boolean(error)} severity="error" message={error ?? ''} onClose={() => setError(null)} />
    </VaultShell>
  );

  return (
    <VaultSessionProvider value={session}>
      {config.paymentsEnabled ? <PaymentsProfileProvider>{body}</PaymentsProfileProvider> : body}
    </VaultSessionProvider>
  );
}

export interface VaultLayoutOutlet {
  selectedAnchor: string | null;
  error: string | null;
  onSelectAnchor: (anchor: string) => void;
  /** Agents in shared recency order (same order the sidebar renders). */
  orderedAgents: AgentConnection[];
  /** Latest vault-wide jobs page — feeds the activity feed. */
  jobs: Job[];
  /** Scroll-spy report: home calls this with the block currently in view. */
  setActiveAnchor: (anchor: string | null) => void;
}
